import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../redux/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../../zodios/client";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import Table from "../../../../modules/merchandising/components/tables/BulkActionsTable";
import TargetDetails from "../../../../modules/advertising/components/TargetDetails";
import KeywordCell from "./KeywordCell";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatCurrency } from "../../../../utils/formatNumber";
import { useMediaQuery } from "@reactuses/core";
import { useFetch } from "../../../../hooks/api";
import { useFormatTargetText } from "../../../../utils/useFormatTargetText";
import { successToast, errorToast } from "../../../../utils/toasts";

import { useTargetLifecycleFilters } from "../../../../modules/advertising/filters/targetLifecycleFilters";
import useCustomTableFilters from "../../../../hooks/useCustomTableFilters";

import {
  AD_TYPES,
  GOALS,
  MIN_BID_DEFAULT,
  WALMART_SB_MIN_BID_DEFAULT,
  WALMART_SBV_MIN_BID_DEFAULT,
} from "../../../core/blocks/AdPlan/constants";
import { useUpdateAdPlan } from "../../../core/blocks/AdPlan/useUpdateAdPlan";
import DownloadReport from "../../../../modules/perf_reports/DownloadReport";
import useTargetBulkActions from "../../../../hooks/useTargetBulkActions";
import { useProductLifecycleFilters } from "../../../../modules/advertising/filters/productLifecycleFilters";
import { TargetBadges } from "./TargetBadges";
import { Button } from "../../../core/basic/Button";
import ExpanderContainer from "../../../core/basic/ExpanderContainer";
import AddTargetsWidget from "./AddTargetsWidget";
import { useSimpleCan } from "../../../../config/casl/casl";

export const TARGET_STATES = {
  PAUSED: { value: "paused", label: "Pause" },
  ENABLED: { value: "enabled", label: "Enable" },
  ARCHIVED: { value: "archived", label: "Archive" },
  NEGATED: { value: "negated", label: "Negated" },
};

export const getScheduledBidChangeValue = (actionValue, actionUnit, actionType) => {
  let result;

  if (actionUnit === "percent") {
    if (actionType === "reduction") {
      result = 1 - parseFloat(actionValue) / 100;
    } else if (actionType === "boost") {
      result = 1 + parseFloat(actionValue) / 100;
    } else if (actionType === "fixed") {
      result = parseFloat(actionValue);
    }
  }

  if (actionUnit === "currency") {
    if (actionType === "reduction") {
      result = parseFloat(actionValue) * -1;
    } else if (["boost", "fixed"].includes(actionType)) {
      result = parseFloat(actionValue);
    }
  }

  return result;
};

const MicroStores2 = ({
  adId,
  tableId,
  targetData,
  isLoading,
  isStatsLoading,
  adPlanData = {},
  adPlanParams = {},
  filters,
}) => {
  const updateAdPlan = useUpdateAdPlan({ adId });
  const { t } = useTranslation(["advertising", "common"]);
  const {
    KEYWORD_MATCH_TYPE_FILTER,
    TARGET_MATCH_TYPE_FILTER,
    TARGET_STATE_FILTER,
    BID_PREVIEW_WARNING_FILTER,
    getTargetBidTypeFilter,
    getTargetSourceFilter,
  } = useTargetLifecycleFilters();
  const { buildCustomTagsFilter } = useProductLifecycleFilters();
  const formatTargetText = useFormatTargetText();
  const [selectedTargets, setSelectedTargets] = useState([]);

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel);

  const acosOrRoas = useAcosOrRoas();
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanExportAds = useSimpleCan("export", "Ad");

  const queryClient = useQueryClient();
  const smallViewportWidthTable = useMediaQuery("(max-width:1550px)");

  const isNTMAd = adPlanData?.ad_goal === GOALS.IMPORTED_ADS.value;
  const NTMEnrolled = adPlanData?.non_trellis_enrolled_ad;
  const NTMBidsAccepted = adPlanData?.non_trellis_enrolled_ad_accept_bids;
  const showPreviewBidColumn = isNTMAd && NTMEnrolled && !NTMBidsAccepted;

  let targetType;

  // Keywords
  switch (tableId) {
    case "keyword":
      targetType = "keyword";
      break;
    case "target":
      targetType = "target";
      break;
    case "placements":
      targetType = "locations";
      break;
    case "adItems":
      targetType = "target";
      break;
    default:
      targetType = "keyword";
      break;
  }

  const tableRef = useRef();

  useEffect(() => {
    resetActionFormFields();
    setSelectedTargets([]);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  // Fetch insights data
  const { data: insightsData } = useFetch(
    ["targetInsights", adId],
    `/target_details`,
    { advertisement: adId, insights: true },
    {
      select: (res) => res.data?.insights ?? [],
      staleTime: 5 * 60 * 1000,
    },
  );

  const updateHandler = useMutation({
    mutationFn: async ({ data }) => {
      return await apiClient.targetAction({ data });
    },

    onSuccess: (data, variables) => {
      // Clear form fields
      resetActionFormFields();
      setSelectedTargets([]);

      successToast({
        message:
          variables?.data?.action === "change_state" ?
            t(($) => $.advertising["{tableEntity} States Successfully Updated"], {
              tableEntity: snakeCaseToSentenceCase(tableId),
            })
          : t(($) => $.advertising["{tableEntity} Bid Settings Successfully Updated"], {
              tableEntity: snakeCaseToSentenceCase(tableId),
            }),
      });

      setSelectedTargets([]);
      if (tableId === "keywords") {
        queryClient.invalidateQueries({
          queryKey: ["keywords_report_meta"],
        });
      } else {
        queryClient.invalidateQueries();
      }
    },

    onError: (error, variables) => {
      errorToast({
        message:
          variables?.data?.action === "change_state" ?
            t(($) => $.advertising["Failed to Update {tableEntity} States"], {
              tableEntity: snakeCaseToSentenceCase(tableId),
            })
          : t(($) => $.advertising["Failed to Update {tableEntity} Bid Settings"], {
              tableEntity: snakeCaseToSentenceCase(tableId),
            }),
        error,
      });
    },
  });

  const updateTargetBidsInline = async (changes) => {
    const editTargetData = targetData?.keywords
      ?.filter((row) =>
        Object.keys(changes)
          ?.map((id) => parseInt(id))
          ?.includes(row.id),
      )
      ?.map((row) => {
        return { ...row, ...changes[row.id] };
      });

    const bids = {};

    for (const editedTarget of editTargetData ?? []) {
      // Stop the whole save on an empty or unparseable input and let the user fix the row.
      const bid = parseFloat(editedTarget.target_bid);
      if (!Number.isFinite(bid)) {
        errorToast({ message: t(($) => $.advertising["Enter a bid value"]) });
        return;
      }

      bids[editedTarget.target_id] = bid;
    }

    if (Object.keys(bids).length === 0) return;

    updateHandler.mutate({
      data: {
        action: "set_bid",
        bids,
        target_type: targetType,
        channel: channel.currentChannel ?? "amazon",
      },
    });
  };

  const tableData = useMemo(() => {
    return (
      targetData?.keywords?.map((target) => {
        return {
          ...target,
          ad_spend: target.cost,
          ...insightsData?.find(
            (insight) =>
              insight?.adv_target === target?.id ||
              insight?.adv_keyword === target?.id ||
              insight?.adv_wmadgkw === target?.id ||
              insight?.adv_wmaditem === target?.id,
          ),
        };
      }) ?? []
    );
  }, [targetData, insightsData]);

  const selectionDisabledIds = tableData
    .filter(
      (target) =>
        target.match_type === "theme" &&
        ![AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adPlanData?.ad_type),
    )
    .map((target) => target.target_id);

  const customTags = useMemo(() => {
    return [...new Set(targetData?.keywords?.flatMap((target) => target.tags))];
  }, [targetData?.keywords]);

  const BID_TYPE_FILTER = getTargetBidTypeFilter(tableId);
  const TARGET_SOURCE_FILTER = getTargetSourceFilter(channel.currentChannel);

  const additionalFilters = [
    ["adItems", "target"].includes(tableId) ? TARGET_MATCH_TYPE_FILTER : KEYWORD_MATCH_TYPE_FILTER,
    BID_TYPE_FILTER,
    TARGET_SOURCE_FILTER,
    TARGET_STATE_FILTER,
    BID_PREVIEW_WARNING_FILTER,
  ];

  if (channel.currentChannel === "amazon") {
    additionalFilters.push(buildCustomTagsFilter(customTags));
  }

  const filterProps = useCustomTableFilters(tableId);

  const ExpandedContext = useCallback(
    ({ row }) => {
      return (
        <ExpanderContainer>
          <TargetDetails
            row={row.row}
            marketPlace={marketPlace}
            channel={channel?.currentChannel}
            targetType={targetType}
          />
        </ExpanderContainer>
      );
    },
    [marketPlace, channel, targetType],
  );

  const { bulkActionOptions, resetActionFormFields } = useTargetBulkActions(
    selectedTargets,
    setSelectedTargets,
    targetType,
    targetData,
    isNTMAd,
    !isNTMAd || (isNTMAd && NTMBidsAccepted),
  );

  const columnSpecs = [
    {
      key: "ad_group_name",
      compareKey: null,
      options: {
        metric: {
          id: "ad_group_name",
          name: t(($) => $.advertising["Ad Group Name"]),
          format: "text",
        },
        toolTip: t(($) => $.advertising["The ad group the {tableId} belongs to"], { tableId }),
        hideFilter: true,
        overflowCell: true,
        additionalStyles: { paddingTop: "20px" },
        formatter: (value) => value,
        checked: isNTMAd,
      },
    },
    {
      key: "target_bid",
      compareKey: null,
      options: {
        editCell: true,
        disabled: !userCanUpdateAds,
        validations: {
          minValue:
            [AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(adPlanData?.ad_type) ? WALMART_SB_MIN_BID_DEFAULT
            : [AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(adPlanData?.ad_type) ? WALMART_SBV_MIN_BID_DEFAULT
            : MIN_BID_DEFAULT,
        },
        columnTitle: t(($) => $.advertising["Bid"]),
        className: "text-end",
        overrideKey: "manual_bid",
        strikeThroughText: (row) => {
          if (!row?._original?.state) {
            return "";
          }
          if (row._original?.state?.toLowerCase()?.indexOf("negative") > -1) {
            return "text-decoration-line-through";
          }
          return "";
        },
        toolTip: t(($) => $.advertising["The current bid for the advertised {tableId}"], { tableId }),
      },
    },
    {
      key: "target_source",
      compareKey: null,
      options: {
        hideFilter: true,
        formatter: (value) =>
          value === "GV_DISCOVERY" ? "initial"
          : value === "harvested" ? "harvesting"
          : value === "harvested_v2" ? "harvesting V2"
          : snakeCaseToSentenceCase(value)?.toLowerCase(),
      },
    },
    {
      key: "creation_date",
      compareKey: null,
      options: {
        metric: {
          id: "creation_date",
          format: "text",
          accessor: (d) => d?.creation_date,
          name: t(($) => $.advertising["Created On"]),
        },
        toolTip: t(($) => $.advertising["The {tableId}'s creation date"], { tableId }),
        formatter: (v) => v ?? "",
        hideFilter: true,
        checked: false,
      },
    },
    {
      key: "match_type",
      compareKey: null,
      options: {
        width: smallViewportWidthTable ? 70 : 150,
        hideFilter: true,
        formatter: (v) => {
          return snakeCaseToSentenceCase(v)?.toLowerCase().indexOf("negative") > -1 ?
              snakeCaseToSentenceCase(v)?.toLowerCase()?.replace("negative", "negative ")
            : snakeCaseToSentenceCase(v)?.toLowerCase();
        },
      },
    },
    {
      key: "target_state",
      compareKey: null,
      options: {
        checked: false,
        metric: {
          id: "target_state",
          format: "text",
          accessor: (d) => d?.target_state,
          name: t(($) => $.advertising["State"]),
          options: { checked: !smallViewportWidthTable },
        },
        toolTip: t(($) => $.advertising["The serving status of the advertised {tableId}"], { tableId }),
        formatter: (v) => snakeCaseToSentenceCase(v)?.toLowerCase() ?? "",
        filterType: "select",
        hideFilter: true,
      },
    },
    {
      key: "target_bid_state",
      compareKey: null,
      options: {
        metric: {
          id: "target_bid_state",
          format: "text",
          accessor: (d) => d?.target_bid_state,
          name: t(($) => $.advertising["Target Bid State"]),
        },
        toolTip: t(($) => $.advertising["The {tableId}'s bid state"], { tableId }),
        formatter: (v) => v?.toLowerCase() ?? "",
        hideFilter: true,
        checked: false,
      },
    },
    { key: "clicks", compareKey: "clicks_percent" },
    { key: "ad_spend", compareKey: "ad_spend_percent" },
    { key: "impressions", compareKey: "impressions_percent" },
    { key: "orders", compareKey: "orders_percent" },
    { key: "ad_sales", compareKey: "ad_sales_percent" },
    acosOrRoas(
      {
        key: "acos",
        compareKey: "acos_delta",
        options: { isNegative: true },
      },
      { key: "roas", compareKey: "roas_delta" },
    ),
    {
      key: "ctr",
      compareKey: "ctr_delta",
      options: {
        checked: false,
      },
    },
    {
      key: "conversion_rate",
      compareKey: "conversion_rate_delta",
      options: {
        checked: false,
      },
    },
    {
      key: "cpc",
      compareKey: "cpc_delta",
      options: {
        checked: false,
        isNegative: true,
      },
    },
  ];

  const previewBidColumn = {
    key: "preview_bid",
    compareKey: null,
    options: {
      columnTitle: t(($) => $.advertising["Bid Preview"]),
      className: "text-end",
      metric: {
        accessor: (d) => d,
      },
      formatter: (v) => {
        const { preview_bid, target_bid } = v;
        const tooHighWarning = preview_bid > target_bid * 1.5;
        const tooLowWarning = preview_bid < target_bid * 0.5;
        return (
          <>
            {(tooHighWarning || tooLowWarning) && (
              <OverlayTrigger
                overlay={
                  <Tooltip placement="top" className="in" id="tooltip-top" bsPrefix="tr-popover-alt bg-purple">
                    <span className="text-center mx-auto fs-5">
                      {tooHighWarning ?
                        t(($) => $.advertising["Bid preview is significantly higher than the current bid"])
                      : t(($) => $.advertising["Bid preview is significantly lower than the current bid"])}
                    </span>
                  </Tooltip>
                }
              >
                <span className="text-danger pe-1">
                  <i className="fa fa-exclamation-circle"></i>
                </span>
              </OverlayTrigger>
            )}
            <span>
              {formatCurrency(preview_bid, {
                marketPlace: marketPlace,
              })}
            </span>
          </>
        );
      },
    },
  };

  if (showPreviewBidColumn) {
    columnSpecs.splice(1, 0, previewBidColumn);
  }

  return (
    <div>
      <Table
        tableRef={tableRef}
        tableId={tableId}
        entityType={"target"}
        titleCol={{
          id: "target",
          isStatic: true,
          Header: t(($) => $.advertising["Target"]),
          accessor: "text",
          style: { width: smallViewportWidthTable ? "200px" : "300px" },
          width: smallViewportWidthTable ? 200 : 300,
          Cell: (props) => {
            return (
              <>
                <KeywordCell
                  value={formatTargetText(props?.original?.text, {
                    ...props.original?.addtl_data,
                    marketPlace: marketPlace,
                  })}
                  columnWidth={300}
                />
                <br />
                <TargetBadges target={props.original} />
              </>
            );
          },

          Filter: () => null,
        }}
        data={tableData}
        // target (keyword, etc.), target type, match type, current bid, clicks, cost, impressions, orders, sales, acos
        columnSpecs={columnSpecs}
        getSearchCriteria={(row) => {
          // Handle keywords with broad match modifier in search filter
          const formattedTargetText = typeof row.text === "string" ? row?.text?.replaceAll("+", "") : "";

          return `${formatTargetText(row?.text)} ${row.id} ${formattedTargetText}`;
        }}
        tableRowDescription={snakeCaseToSentenceCase(targetType)}
        defaultSorted={[{ id: "target_bid", desc: true }]}
        isLoading={isLoading}
        isStatsLoading={isStatsLoading}
        selected={selectedTargets}
        hideSelectAll={adPlanData.serving_status === "ENDED" || !userCanUpdateAds}
        hideBulkActionsButton={adPlanData.serving_status === "ENDED" || !userCanUpdateAds}
        isSelectable={adPlanData.serving_status !== "ENDED" && userCanUpdateAds}
        setSelected={setSelectedTargets}
        selectionKey="target_id"
        disableSelectionList={selectionDisabledIds}
        bulkActionOptions={bulkActionOptions}
        filterWidget={true}
        hideFilterToggleIcon={true}
        hideResetFiltersIcon={false}
        {...filterProps}
        additionalFilterOptions={additionalFilters}
        excludedFilterColumns={["match_type", "target_state", "target_source"]}
        handleBulkChanges={(changes) => updateTargetBidsInline(changes)}
        ExpandedContent={ExpandedContext}
        children={
          <>
            {isStatsLoading && (
              <span className="fs-standard">
                <i className="fa fa-spin fa-circle-o-notch"> </i>
                {t(($) => $.advertising["Loading Stats"])}
              </span>
            )}
          </>
        }
        additionalFilters={
          showPreviewBidColumn ?
            <Button isDisabled={updateAdPlan.isPending} onPress={() => updateAdPlan.mutate({ adPlan: adPlanParams })}>
              {t(($) => $.advertising["Refresh Bid Preview"])}
            </Button>
          : null
        }
        leftActionButton={
          (
            ((isNTMAd && NTMBidsAccepted) || !isNTMAd) &&
            !(channel.currentChannel === "walmart" && tableId === "adItems") &&
            userCanUpdateAds
          ) ?
            <AddTargetsWidget
              key={`${adId}${targetType}`}
              adId={adId}
              adType={adPlanData?.ad_type}
              targetType={targetType}
              isNTMAd={isNTMAd}
            />
          : null
        }
      />
      {userCanExportAds && (
        <div className="text-right">
          <DownloadReport
            url_path={"keyword_report"}
            filters={{
              ...filters,
              empty: true,
              kw_report: false,
              target_type: tableId === "adItems" ? "target" : tableId,
            }}
            report_dl={"keywords"}
          />
        </div>
      )}
    </div>
  );
};

export default MicroStores2;
